import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { RpaBotsService } from 'app/rpa-bots/core/rpa-bots.service';
import {
  EAutomationAnywhereConnActions,
  AutomationAnywhereConnList,
  AutomationAnywhereConnListSuccess,
  UpdateAutomationAnywhereConn,
  UpdateAutomationAnywhereConnSuccess,
  GetCurrentAutomationAnywhereConn,
  GetCurrentAutomationAnywhereConnSuccess,
  GetAutomationAnywhereBots,
  GetAutomationAnywhereDevices,
  GetAutomationAnywherePoolDevices,
  GetAutomationAnywhereUsers,
  ShowSuccessToast,
  TestAutomationAnywhereConn,
  TestAutomationAnywhereConnSuccess,
  GetVersionAutomationAnywhere,
  GetVersionAutomationAnywhereSuccess,
  SetAABotsSuccess,
  SetAADevicesSuccess,
  SetAAPoolDevicesSuccess,
  SetAAUsersSuccess
} from 'app/rpa-bots/automation-anywhere/store/actions/rpa-automation-anywhere-conn.actions';
import { switchMap, catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { of, EMPTY } from 'rxjs';
import { IAppState } from 'app/IAppState';
import { IRPAAutomationAnywhereBot, IRPAAutomationAnywhereDevice, IRPAAutomationAnywherePoolDevice, IRPAAutomationAnywhereUser, IRpaConnectionAutomationAnywhere } from 'app/rpa-bots/core/IRpaBots';
import { BzgToastService, BzgToastType } from 'bz-genome-angular';
import { TranslateService } from '@ngx-translate/core';
import { I18nService } from 'i18n/i18n.service';
import { ShowToastService } from 'app/shared/services/showToast.service';

@Injectable()
export class AutomationAnywhereConnEffects {

  getAutomationAnywhereConnList$ = createEffect(() =>
    this._actions$.pipe(
      ofType<AutomationAnywhereConnList>(
        EAutomationAnywhereConnActions.AutomationAnywhereConnList
      ),
      switchMap(() => this._rpaBotsService.getConnectionsAutomationAnywhere()),
      switchMap((connectors: IRpaConnectionAutomationAnywhere[]) =>
        of(new AutomationAnywhereConnListSuccess(connectors))
      )
    )
  );
  getAutomationAnywhereBots$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetAutomationAnywhereBots>(
        EAutomationAnywhereConnActions.GetAutomationAnywhereBots
      ),
      switchMap(param =>
        this._rpaBotsService
          .getBotsAutomationAnywhere(param.payload)
          .pipe(
            catchError(err => {
              this._store.dispatch(new SetAABotsSuccess(null));
              return 'ERROR';
            })
          )
          .switchMap((bots: IRPAAutomationAnywhereBot[] | string) =>
            typeof bots === 'string' ? null : of(new SetAABotsSuccess(bots))
          )
      )
    )
  );
  getAutomationAnywhereDevices$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetAutomationAnywhereDevices>(
        EAutomationAnywhereConnActions.GetAutomationAnywhereDevices
      ),
      switchMap(param =>
        this._rpaBotsService
          .getDevicesAutomationAnywhere(param.payload)
          .pipe(
            catchError(err => {
              this._store.dispatch(new SetAADevicesSuccess(null));
              return 'ERROR';
            })
          )
          .switchMap((devices: IRPAAutomationAnywhereDevice[] | string) =>
            typeof devices === 'string'
              ? null
              : of(new SetAADevicesSuccess(devices))
          )
      )
    )
  );
  getVersionAutomationAnywhere$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetVersionAutomationAnywhere>(
        EAutomationAnywhereConnActions.GetVersionAutomationAnywhere
      ),
      switchMap(param =>
        this._rpaBotsService
          .getVersionAutomationAnywhere(param.payload)
          .pipe(
            catchError(err => {
              this._store.dispatch(new GetVersionAutomationAnywhereSuccess(null));
              return 'ERROR';
            })
          )
          .switchMap((version: string) =>
            version === 'ERROR'
              ? EMPTY
              : of(new GetVersionAutomationAnywhereSuccess(version))
          )
      )
    )
  );
  getAutomationAnywherePoolDevices$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetAutomationAnywherePoolDevices>(
        EAutomationAnywhereConnActions.GetAutomationAnywherePoolDevices
      ),
      switchMap(param =>
        this._rpaBotsService
          .getPoolDevicesAutomationAnywhere(param.payload)
          .pipe(
            catchError(err => {
              this._store.dispatch(new SetAAPoolDevicesSuccess(null));
              return 'ERROR';
            })
          )
          .switchMap((poolDevices: IRPAAutomationAnywherePoolDevice[] | string) =>
            typeof poolDevices === 'string'
              ? null
              : of(new SetAAPoolDevicesSuccess(poolDevices))
          )
      )
    )
  );
  getAutomationAnywhereUsers$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetAutomationAnywhereUsers>(
        EAutomationAnywhereConnActions.GetAutomationAnywhereUsers
      ),
      switchMap(param =>
        this._rpaBotsService
          .getUsersAutomationAnywhere(param.payload)
          .pipe(
            catchError(err => {
              this._store.dispatch(new SetAAUsersSuccess(null));
              return 'ERROR';
            })
          )
          .switchMap((users: IRPAAutomationAnywhereUser[] | string) =>
            typeof users === 'string' ? null : of(new SetAAUsersSuccess(users))
          )
      )
    )
  );
  updateAutomationAnywhereConn$ = createEffect(() =>
    this._actions$.pipe(
      ofType<UpdateAutomationAnywhereConn>(
        EAutomationAnywhereConnActions.UpdateAutomationAnywhereConn
      ),
      switchMap(param =>
        this._rpaBotsService.updateConnectionAutomationAnywhere(param.payload)
      ),
      switchMap(() => [
        new UpdateAutomationAnywhereConnSuccess(),
        new ShowSuccessToast(),
        new AutomationAnywhereConnList()
      ])
    )
  );
  currentAutomationAnywhereConn$ = createEffect(() =>
    this._actions$.pipe(
      ofType<GetCurrentAutomationAnywhereConn>(
        EAutomationAnywhereConnActions.GetCurrentAutomationAnywhereConn
      ),
      switchMap(param =>
        this._rpaBotsService.getConnectionAutomationAnywhere(param.payload)
      ),
      switchMap((connector: IRpaConnectionAutomationAnywhere) =>
        of(new GetCurrentAutomationAnywhereConnSuccess(connector))
      )
    )
  );
  testAutomationAnywhereConnection$ = createEffect(() =>
    this._actions$.pipe(
      ofType<TestAutomationAnywhereConn>(
        EAutomationAnywhereConnActions.TestAutomationAnywhereConn
      ),
      switchMap(param =>
        this._rpaBotsService.testConnectionAutomationAnywhere(param.payload)
      ),
      switchMap(() => {
        this._showToastService.showToast(this._i18nService.instant('RPA_BOTS.TEST_CONNECTION_SUCCESS'), 'success');
        return of(new TestAutomationAnywhereConnSuccess());
      }),
      catchError((error, caught) => {
        this._store.dispatch(new TestAutomationAnywhereConnSuccess());
        return caught;
      })
    )
  );

  showSuccessToast$ = createEffect(() =>
    this._actions$.pipe(
      ofType<ShowSuccessToast>(EAutomationAnywhereConnActions.ShowSuccessToast),
      switchMap(() => {
        this._showToastService.showToast(this._i18nService.instant('GLOBAL.THE_OPERATION_WAS_SAVED_SUCCESSFULLY'), 'success');
        return of(new ShowSuccessToast());
      })
    )
  );

  constructor(
    private _rpaBotsService: RpaBotsService,
    private _actions$: Actions,
    private _store: Store<IAppState>,
    private _translateService: TranslateService,
    private _toastService: BzgToastService,
    private _i18nService: I18nService,
    private _showToastService: ShowToastService,
  ) {
  }
}
